import {
  globalConfig,
  globalManager,
  ViewWindow,
  globalTools,
} from "@/utils/globalConfig.ts";
import { sceneUtils } from "@/utils/sceneUtils";
import {
  setSelectionDropdown,
  getSelectionDropdown,
  setSwitchCheckbox,
  openExampleViewPage,
  getI18nText,
} from "@/utils/commonFunc";
import { animationUtils } from "@/utils/animationUtils";
import { SecureDirectoryPicker } from "@/utils/filePicker";
import i18next from "i18next";
import { getSrcByImgFile } from "@/utils/fileUtils";
import marble4ApiController, {
  TextWorldPrompt,
} from "@/visionaryControllers/marble4ApiController";
import marbleApiController from "@/visionaryControllers/marbleApiController";
import { createShowMsgUI, ShowMsgUI } from "./base/ui/showMsgUI";
import { VisionaryScene } from "@/visionaryControllers/visionaryScene";

declare const bootstrap: any;

// File System Access API types
declare global {
  interface Window {
    showDirectoryPicker(options?: {
      mode?: "read" | "readwrite";
    }): Promise<FileSystemDirectoryHandle>;
  }

  interface FileSystemDirectoryHandle {
    name: string;
    queryPermission(descriptor?: {
      mode?: "read" | "readwrite";
    }): Promise<PermissionState>;
    requestPermission(descriptor?: {
      mode?: "read" | "readwrite";
    }): Promise<PermissionState>;
  }
}

interface MarbleMediaFile {
  id: string;
  file: File;
  media_asset_id: string;
  metadata: Record<string, unknown>;
  kind: "image" | "video";
  extension: string;
  file_name: string;
  file_src: string;
  azimuth: number;
}

interface MarbleModelInfo {
  id: string;
  name: string | "";
  image: string | "";
  status: "pending" | "running" | "completed" | "failed";
  srcurl: string | "";
  textPrompt: string | "";
  created_at: string | "";
  updated_at: string | "";
}

export class ModelDialogTools {
  private videoSettingsModalDom: HTMLElement | null = null;
  private imageSettingsModalDom: HTMLElement | null = null;
  private sceneSettingsModalDom: HTMLElement | null = null;
  private sceneSaveModalDom: HTMLElement | null = null;
  private marbleImportModalDom: HTMLElement | null = null;
  private marbleImportMediaListDom: HTMLElement | null = null;
  private marbleModelListModalDom: HTMLElement | null = null;
  private marbleHistoryLoadMoreBtn: HTMLElement | null = null;
  private marbleHistoryListBodyDom: HTMLElement | null = null;
  private demoViewModalDom: HTMLElement | null = null;
  private exitViewModalDom: HTMLElement | null = null;
  private loadingInfoModalDom: HTMLElement | null = null;
  private confirmModalDom: HTMLElement | null = null;
  private confirmModalInstance: any = null;
  private confirmCancelHandler: ((event: Event) => void) | null = null;
  private confirmConfirmHandler: ((event: Event) => void) | null = null;
  private confirmCloseHandler: ((event: Event) => void) | null = null;
  private confirmHiddenHandler: ((event: Event) => void) | null = null;
  private confirmOnCancel: (() => void) | null = null;
  private confirmLastAction: "confirm" | "cancel" | "dismiss" = "dismiss";

  private showMessageModalDom: HTMLElement | null = null;
  private showMessageModalInstance: any = null;
  private showMessageModalTimeout: NodeJS.Timeout | null = null;
  private showMessageModalConfirmHandler: ((event: Event) => void) | null =
    null;
  private showMessageModalCloseHandler: ((event: Event) => void) | null = null;
  private showMessageModalHiddenHandler: ((event: Event) => void) | null = null;

  private sceneSaveModalFolderHandle: FileSystemDirectoryHandle | null = null;

  private marbleListScene: VisionaryScene | null = null;
  private marbleImportMediaFiles: MarbleMediaFile[] = [];
  private marbleModelListPageToken: string | null = null;
  private marbleKeyCheckRequestId = 0;

  private showMsgUI: ShowMsgUI | null = null;
  constructor() {
    this.videoSettingsModalDom = document.getElementById("videoSettingsModal");
    this.imageSettingsModalDom = document.getElementById("imgSettingsModal");
    this.sceneSettingsModalDom = document.getElementById("sceneModal");
    this.demoViewModalDom = document.getElementById("demoViewModal");
    this.sceneSaveModalDom = document.getElementById("saveSceneSettingsModal");
    this.marbleImportModalDom = document.getElementById("marbleImportModal");
    this.marbleImportMediaListDom = document.getElementById(
      "marbleImportMediaList"
    );
    this.marbleModelListModalDom = document.getElementById(
      "marbleModelListModal"
    );
    this.marbleHistoryLoadMoreBtn = document.getElementById(
      "marbleHistoryLoadMoreBtn"
    );
    this.marbleHistoryListBodyDom = document.getElementById(
      "marbleHistoryListBody"
    );
    this.exitViewModalDom = document.getElementById("exitViewModal");
    this.loadingInfoModalDom = document.getElementById("loadingInfoModal");
    this.confirmModalDom = document.getElementById("modelDeleteConfirmModal");
    this.showMessageModalDom = document.getElementById("modelShowMessageModal");
    if (
      !this.videoSettingsModalDom ||
      !this.imageSettingsModalDom ||
      !this.sceneSettingsModalDom ||
      !this.sceneSaveModalDom ||
      !this.marbleImportModalDom ||
      !this.marbleImportMediaListDom ||
      !this.demoViewModalDom ||
      !this.exitViewModalDom ||
      !this.loadingInfoModalDom ||
      !this.confirmModalDom ||
      !this.showMessageModalDom
    ) {
      throw new Error("Modal elements not found");
    }
    this.setupEventHandlers();
  }
  setupEventHandlers() {
    this.initNoticeMessageDialog();
    this.initAboutMessageDialog();
    this.initOpenNewSceneDialog();
    this.initSaveImageDialog();
    this.initSaveVideoDialog();
    this.initSaveSceneDialog();
    this.initMarbleImportDialog();
    this.initMarbleModelListDialog();
    this.initDemoViewDialog();
    this.initExistViewDialog();

    // ESC 键关闭 注意：loadingInfoModal 通常不响应 ESC 键，因为它是加载状态提示
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        if (
          this.videoSettingsModalDom &&
          this.videoSettingsModalDom.classList.contains("vision-modal-show")
        ) {
          this.closeVideoDialog();
        }
        if (
          this.imageSettingsModalDom &&
          this.imageSettingsModalDom.classList.contains("vision-modal-show")
        ) {
          this.closeImageDialog();
        }
        if (
          this.sceneSettingsModalDom &&
          this.sceneSettingsModalDom.classList.contains("vision-modal-show")
        ) {
          this.closeSceneDialog();
        }
        if (
          this.sceneSaveModalDom &&
          this.sceneSaveModalDom.classList.contains("vision-modal-show")
        ) {
          this.closeSaveSceneDialog();
        }
        if (
          this.marbleImportModalDom &&
          this.marbleImportModalDom.classList.contains("vision-modal-show")
        ) {
          this.closeMarbleImportDialog();
        }
        if (
          this.marbleModelListModalDom &&
          this.marbleModelListModalDom.classList.contains("vision-modal-show")
        ) {
          this.closeMarbleModelListDialog();
        }
        if (
          this.demoViewModalDom &&
          this.demoViewModalDom.classList.contains("vision-modal-show")
        ) {
          this.closeDemoViewDialog();
        }
        if (
          this.exitViewModalDom &&
          this.exitViewModalDom.classList.contains("vision-modal-show")
        ) {
          this.closeExitViewDialog();
        }
        if (
          this.showMessageModalDom &&
          (this.showMessageModalDom.classList.contains("vision-modal-show") ||
            this.showMessageModalDom.classList.contains("show"))
        ) {
          this.closeShowMessageModal();
        }
      }
    });
  }

  initNoticeMessageDialog() {
    const noticeMessageConfirmBtn = document.getElementById(
      "noticeMessageConfirmBtn"
    );
    if (noticeMessageConfirmBtn) {
      noticeMessageConfirmBtn.addEventListener("click", () => {
        // // 获取复选框状态 - 是否不再显示提示，记住用户选择
        // const noticeNotShowAgainCheckbox = document.getElementById('noticeNotShowAgain') as HTMLInputElement | null;
        // const notShowAgain = noticeNotShowAgainCheckbox ? noticeNotShowAgainCheckbox.checked : false;

        // // 保存到 localStorage
        // if (notShowAgain) {
        //     localStorage.setItem('noticeMessageNotShowAgain', 'true');
        // } else {
        //     localStorage.removeItem('noticeMessageNotShowAgain');
        // }
        globalConfig.confirmNoticeMessageDialog = true;
        this.setNoticeMessageModal(false);
      });
    }
  }

  initAboutMessageDialog() {
    const aboutMessageConfirmBtn = document.getElementById(
      "aboutMessageCloseBtn"
    );
    if (aboutMessageConfirmBtn) {
      aboutMessageConfirmBtn.addEventListener("click", () => {
        this.setAboutMessageModal(false);
      });
    }
  }
  // 新建场景对话框
  initOpenNewSceneDialog() {
    if (!this.sceneSaveModalDom) return;
    const sceneImageItems = document.querySelectorAll(
      ".vision-scene-modal-dialog .vision-scene-image-item"
    );
    if (!sceneImageItems || sceneImageItems.length === 0) return;

    // 选择示例场景
    sceneImageItems.forEach((item) => {
      item.addEventListener("click", () => {
        const video = item.querySelector("video") as HTMLVideoElement | null;
        if (!video) return;
        // const dataUrl = (video.dataset && (video.dataset as Record<string, string>).url) || video.getAttribute('data-scene') || video.src;
        const dataScene = video.getAttribute("data-scene") || "";
        if (dataScene) {
          openExampleViewPage(dataScene);
        }
      });
    });

    const newBlankSceneBtn = document.getElementById("newBlankSceneBtn");
    if (newBlankSceneBtn) {
      newBlankSceneBtn.addEventListener("click", () => {
        console.log("新建空白场景");
        sceneUtils.createNewScene();
        this.closeSceneDialog();
      });
    }

    const uploadLocalSceneBtn = document.getElementById("uploadLocalSceneBtn");
    if (uploadLocalSceneBtn) {
      uploadLocalSceneBtn.addEventListener("click", () => {
        console.log("上传本地场景");
        sceneUtils.uploadLocalScene();
        this.closeSceneDialog();
      });
    }
  }
  // 保存图片对话框
  initSaveImageDialog() {
    const imgSettingsConfirm = document.getElementById("imgSettingsConfirm");

    if (this.imageSettingsModalDom) {
      setSwitchCheckbox("image-linedrawing-control"); // 初始化复选框

      // 初始化视窗设置单选框
      const imgSettingsViewWindow = document.getElementById(
        "imgSettingsViewWindow"
      );
      const viewOptionItems =
        imgSettingsViewWindow?.querySelectorAll(
          ".vision-img-view-option-item"
        ) || [];
      let selectedViewValue = "left"; // 默认选中"左"

      // 重置选择，默认选中"左"
      selectedViewValue = "left";
      viewOptionItems.forEach((item) => {
        const el = item as HTMLElement;
        if (el.dataset.value === selectedViewValue) {
          el.classList.add("vision-img-view-option-selected");
        } else {
          el.classList.remove("vision-img-view-option-selected");
        }
      });

      // 选择视窗选项
      viewOptionItems.forEach((item) => {
        const el = item as HTMLElement;
        el.addEventListener("click", () => {
          selectedViewValue = el.dataset.value || "left";
          viewOptionItems.forEach((opt) =>
            (opt as HTMLElement).classList.remove(
              "vision-img-view-option-selected"
            )
          );
          el.classList.add("vision-img-view-option-selected");
        });
      });

      const imgSettingsCancel = document.getElementById("imgSettingsCancel");
      if (imgSettingsCancel) {
        imgSettingsCancel.addEventListener("click", () => {
          this.closeImageDialog();
        });
      }

      if (imgSettingsConfirm) {
        imgSettingsConfirm.addEventListener("click", () => {
          const width = (
            document.getElementById(
              "imgResolutionWidth"
            ) as HTMLInputElement | null
          )?.value;
          const height = (
            document.getElementById(
              "imgResolutionHeight"
            ) as HTMLInputElement | null
          )?.value;

          // 获取复选框状态
          const enableLineDrawingCheckbox = document.getElementById(
            "image-linedrawing-control"
          ) as HTMLInputElement | null;
          const enableLineDrawing = enableLineDrawingCheckbox
            ? enableLineDrawingCheckbox.checked
            : false;

          let currentSelectWindow: ViewWindow = selectedViewValue as ViewWindow;
          // 获取当前选中的视窗设置
          if (globalManager.getViewWindowIfSingle()) {
            currentSelectWindow = "left" as ViewWindow;
          }
          this.closeImageDialog();
          sceneUtils.saveImage(
            Number(width || 1),
            Number(height || 1),
            enableLineDrawing,
            currentSelectWindow
          );
        });
      }

      // 点击遮罩层关闭
      this.imageSettingsModalDom.addEventListener("click", (e) => {
        if (e.target === this.imageSettingsModalDom) {
          this.closeImageDialog();
        }
      });

      // ESC 键关闭
      document.addEventListener("keydown", (e) => {
        if (
          e.key === "Escape" &&
          this.imageSettingsModalDom &&
          this.imageSettingsModalDom.classList.contains("vision-modal-show")
        ) {
          this.closeImageDialog();
        }
      });
    }
  }
  // 保存视频对话框
  initSaveVideoDialog() {
    const videoSettingsCancel = document.getElementById("videoSettingsCancel");
    const videoSettingsConfirm = document.getElementById(
      "videoSettingsConfirm"
    );

    if (this.videoSettingsModalDom) {
      setSelectionDropdown("videoResolution");
      setSelectionDropdown("videoFramePS");
      setSelectionDropdown("videoBitrate");
      setSwitchCheckbox("video-linedrawing-control"); // 初始化复选框

      // 初始化视窗设置单选框
      const videoSettingsViewWindow = document.getElementById(
        "videoSettingsViewWindow"
      );
      const viewOptionItems =
        videoSettingsViewWindow?.querySelectorAll(
          ".vision-img-view-option-item"
        ) || [];
      let selectedViewValue = "left";

      // 重置选择，默认选中"左"
      selectedViewValue = "left";
      viewOptionItems.forEach((item) => {
        const el = item as HTMLElement;
        if (el.dataset.value === selectedViewValue) {
          el.classList.add("vision-img-view-option-selected");
        } else {
          el.classList.remove("vision-img-view-option-selected");
        }
      });

      // 选择视窗选项
      viewOptionItems.forEach((item) => {
        const el = item as HTMLElement;
        el.addEventListener("click", () => {
          selectedViewValue = el.dataset.value || "left";
          viewOptionItems.forEach((opt) =>
            (opt as HTMLElement).classList.remove(
              "vision-img-view-option-selected"
            )
          );
          el.classList.add("vision-img-view-option-selected");
        });
      });
      // 取消按钮
      if (videoSettingsCancel) {
        videoSettingsCancel.addEventListener("click", () => {
          this.closeVideoDialog();
        });
      }

      // 确认按钮
      if (videoSettingsConfirm) {
        videoSettingsConfirm.addEventListener("click", () => {
          let resolution = { width: 1920, height: 1080 };
          let fps = 30;
          let bitrate = "high";

          const resolutionItem = getSelectionDropdown("videoResolution") as
            | string
            | null;
          if (resolutionItem) {
            resolution = globalConfig.videoSelectItems.resolution[
              resolutionItem as keyof typeof globalConfig.videoSelectItems.resolution
            ] || { width: 1920, height: 1080 };
          }

          const fpsItem = getSelectionDropdown("videoFramePS") as string | null;
          if (fpsItem) {
            fps =
              globalConfig.videoSelectItems.fps[
                fpsItem as keyof typeof globalConfig.videoSelectItems.fps
              ] || 30;
          }

          const bitrateItem = getSelectionDropdown("videoBitrate") as
            | string
            | null;
          if (bitrateItem) {
            bitrate =
              globalConfig.videoSelectItems.bitrate[
                bitrateItem as keyof typeof globalConfig.videoSelectItems.bitrate
              ] || "high";
          }

          // 获取复选框状态
          const enableLineDrawingCheckbox = document.getElementById(
            "video-linedrawing-control"
          ) as HTMLInputElement | null;
          const enableLineDrawing = enableLineDrawingCheckbox
            ? enableLineDrawingCheckbox.checked
            : false;

          const duration = animationUtils.getTotalFrames() / fps;

          let currentSelectWindow: ViewWindow = selectedViewValue as ViewWindow;
          // 获取当前选中的视窗设置
          if (globalManager.getViewWindowIfSingle()) {
            currentSelectWindow = "left" as ViewWindow;
          }
          this.closeVideoDialog();
          sceneUtils.saveVideo(
            {
              resolution,
              fps: Number(fps),
              bitrate,
              duration: Number(duration),
              enableLineDrawing,
            },
            currentSelectWindow
          );
        });
      }

      // 点击遮罩层关闭
      this.videoSettingsModalDom.addEventListener("click", (e) => {
        if (e.target === this.videoSettingsModalDom) {
          this.closeVideoDialog();
        }
      });

      // ESC 键关闭
      document.addEventListener("keydown", (e) => {
        if (
          e.key === "Escape" &&
          this.videoSettingsModalDom &&
          this.videoSettingsModalDom.classList.contains("vision-modal-show")
        ) {
          this.closeVideoDialog();
        }
      });
    }
  }

  // 保存场景对话框
  initSaveSceneDialog() {
    if (!this.sceneSaveModalDom) return;

    // 设置文件夹输入框为只读
    const saveSceneFolderInput = document.getElementById(
      "saveSceneFolderInput"
    ) as HTMLInputElement | null;
    if (saveSceneFolderInput) {
      saveSceneFolderInput.readOnly = true;
    }

    // 场景名称输入框 - 禁止事件冒泡
    const saveSceneNameInput = document.getElementById(
      "saveSceneNameInput"
    ) as HTMLInputElement | null;
    if (saveSceneNameInput) {
      // 阻止所有可能冒泡的事件
      const stopPropagation = (e: Event) => {
        e.stopPropagation();
      };
      saveSceneNameInput.addEventListener("click", stopPropagation);
      saveSceneNameInput.addEventListener("mousedown", stopPropagation);
      saveSceneNameInput.addEventListener("mouseup", stopPropagation);
      saveSceneNameInput.addEventListener("input", stopPropagation);
      saveSceneNameInput.addEventListener("keydown", stopPropagation);
      saveSceneNameInput.addEventListener("keyup", stopPropagation);
    }

    // 选择文件夹按钮
    const saveSceneFolderButton = document.getElementById(
      "saveSceneFolderButton"
    );
    if (saveSceneFolderButton) {
      saveSceneFolderButton.addEventListener("click", async () => {
        const sceneFS = globalManager.getSceneFS();
        if (!sceneFS) {
          console.warn("[Scene] SceneFS 未初始化，无法打开场景");
          this.showMsgDialog(getI18nText("errorinfo.sceneFSNotAvailable"));
          return;
        }
        try {
          // 使用 SecureDirectoryPicker 来处理 iframe 场景
          const picker = new SecureDirectoryPicker();
          const handle = await picker.show({
            mode: "readwrite",
          });

          if (!handle) {
            console.error("No directory handle returned");
            return;
          }

          await sceneFS.ensurePermission(handle, "readwrite");
          // 保存文件夹 handle
          this.sceneSaveModalFolderHandle = handle;

          // 在输入框中显示文件夹名称
          if (saveSceneFolderInput) {
            saveSceneFolderInput.value = handle.name;
          }

          console.log("SceneFS: Folder selected for save:", handle.name);
        } catch (error) {
          if ((error as Error).name === "AbortError") {
            console.log("Folder selection cancelled by user");
          } else {
            console.error("Failed to select folder:", error);
            // 如果是跨域错误，提示用户
            if (
              (error as Error).message.includes("Cross origin") ||
              (error as Error).message.includes("sub frames")
            ) {
              this.showMsgDialog(
                getI18nText("errorinfo.iframeFolderPickerError")
              );
            }
          }
        }
      });
    }

    // 取消按钮
    const saveSceneSettingsCancel = document.getElementById(
      "saveSceneSettingsCancel"
    );
    if (saveSceneSettingsCancel) {
      saveSceneSettingsCancel.addEventListener("click", () => {
        this.closeSaveSceneDialog();
      });
    }

    // 确认按钮
    const saveSceneSettingsConfirm = document.getElementById(
      "saveSceneSettingsConfirm"
    );
    if (saveSceneSettingsConfirm) {
      saveSceneSettingsConfirm.addEventListener("click", async () => {
        const saveSceneNameInput = document.getElementById(
          "saveSceneNameInput"
        ) as HTMLInputElement | null;
        const sceneName = saveSceneNameInput
          ? saveSceneNameInput.value.trim()
          : "";

        if (this.sceneSaveModalFolderHandle && sceneName) {
          try {
            const sceneFS = globalManager.getSceneFS();
            if (!sceneFS) {
              console.warn("[Scene] SceneFS 未初始化，无法保存场景");
              this.showMsgDialog(getI18nText("errorinfo.sceneFSNotAvailable"));
              this.closeSaveSceneDialog();
              return;
            }

            // 检查子文件夹是否存在，如果不存在则创建
            let sceneFolderHandle: FileSystemDirectoryHandle;
            try {
              // 尝试获取已存在的文件夹
              sceneFolderHandle =
                await this.sceneSaveModalFolderHandle.getDirectoryHandle(
                  sceneName
                );
              console.log(`[Scene] 找到已存在的场景文件夹: ${sceneName}`);
            } catch (error) {
              // 如果文件夹不存在，创建它
              try {
                sceneFolderHandle =
                  await this.sceneSaveModalFolderHandle.getDirectoryHandle(
                    sceneName,
                    { create: true }
                  );
                console.log(`[Scene] 创建新的场景文件夹: ${sceneName}`);
              } catch (createError) {
                console.error("[Scene] 创建场景文件夹失败:", createError);
                this.showMsgDialog(
                  getI18nText("errorinfo.createSceneFolderFailed", {
                    error: (createError as Error).message,
                  })
                );
                this.closeSaveSceneDialog();
                return;
              }
            }

            // 确保子文件夹拥有读写权限
            await sceneFS.ensurePermission(sceneFolderHandle, "readwrite");

            // 使用子文件夹的 handle 保存场景
            sceneUtils.saveScene(sceneFolderHandle);
          } catch (error) {
            console.error("[Scene] 保存场景前处理失败:", error);
            this.showMsgDialog(
              getI18nText("errorinfo.processSceneFolderFailed", {
                error: (error as Error).message,
              })
            );
            this.closeSaveSceneDialog();
            return;
          }
        }

        this.closeSaveSceneDialog();
      });
    }

    // 点击遮罩层关闭
    this.sceneSaveModalDom.addEventListener("click", (e) => {
      if (e.target === this.sceneSaveModalDom) {
        this.closeSaveSceneDialog();
      }
    });
  }

  /**
   * 获取媒体资源上传配置，用来控制上传限制：图片不超过4张，视频不超过1个
   * @returns { accept: string, canUpload: boolean, maxUploadCount: number }
   * accept: 上传文件的类型
   * canUpload: 是否可以上传
   * maxUploadCount: 最大上传数量
   */
  /** 图片 accept：jpg、jpeg、png、webp；视频 accept：mp4、mov、mkv */
  // static readonly MARBLE_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
  // static readonly MARBLE_VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/x-matroska,.mp4,.mov,.mkv';
  static readonly MARBLE_IMAGE_ACCEPT = ".jpg,.jpeg,.png,.webp";
  static readonly MARBLE_VIDEO_ACCEPT = ".mp4,.mov,.mkv";
  static readonly MARBLE_MEDIA_ACCEPT = `${ModelDialogTools.MARBLE_IMAGE_ACCEPT},${ModelDialogTools.MARBLE_VIDEO_ACCEPT}`;

  getMarbleMediaInputConfig(): {
    accept: string;
    canUpload: boolean;
    maxUploadCount: number;
  } {
    let accept = ModelDialogTools.MARBLE_MEDIA_ACCEPT;
    let canUpload = true;
    let maxUploadCount = 4;
    if (this.marbleImportMediaFiles.length > 0) {
      if (this.marbleImportMediaFiles.length >= 4) {
        canUpload = false;
        maxUploadCount = 0;
      }
      accept = ModelDialogTools.MARBLE_IMAGE_ACCEPT;
      maxUploadCount = 4 - this.marbleImportMediaFiles.length;
      this.marbleImportMediaFiles.forEach((fileitem) => {
        if (fileitem.kind === "video") {
          canUpload = false;
          maxUploadCount = 0;
        }
      });
    } else {
      accept = ModelDialogTools.MARBLE_MEDIA_ACCEPT;
      maxUploadCount = 4;
    }
    return {
      accept: accept,
      canUpload: canUpload,
      maxUploadCount: maxUploadCount,
    };
  }

  public updateMarbleImportMediaList() {
    const uploadDom = document.getElementById("marbleImportMediaUploadDom");
    const listDom = document.getElementById("marbleImportMediaListDom");
    if (this.marbleImportMediaFiles.length === 0) {
      if (uploadDom) {
        uploadDom.style.display = "block";
      }
      if (listDom) {
        listDom.style.display = "none";
      }
    } else {
      if (uploadDom) {
        uploadDom.style.display = "none";
      }
      if (listDom) {
        listDom.style.display = "flex";
      }
      const mediaListDom = document.getElementById("marbleImportMediaList");
      if (mediaListDom) {
        mediaListDom.innerHTML = "";

        this.marbleImportMediaFiles.forEach((fileitem, index) => {
          const mediaItemDom = document.createElement("div");
          mediaItemDom.classList.add("marble-import-media-item");
          mediaItemDom.setAttribute("data-media-id", fileitem.id);

          const mediaItemImgDom = document.createElement("img");
          mediaItemImgDom.classList.add("marble-import-media-item-img");
          mediaItemImgDom.src = fileitem.file_src;
          mediaItemDom.appendChild(mediaItemImgDom);

          const mediaItemAzimuthSelectDom = document.createElement("select");
          mediaItemAzimuthSelectDom.classList.add(
            "marble-import-media-item-azimuth-select"
          );
          const azimuthOptions: Array<{ value: number; label: string }> = [
            { value: 0, label: "Front" },
            { value: 1, label: "Back" },
            { value: 2, label: "Right" },
            { value: 3, label: "Left" },
          ];
          azimuthOptions.forEach((option) => {
            const optionDom = document.createElement("option");
            optionDom.value = option.value.toString();
            optionDom.textContent = `${option.label}`;
            mediaItemAzimuthSelectDom.appendChild(optionDom);
          });
          const currentAzimuth = Number(fileitem.azimuth);
          mediaItemAzimuthSelectDom.value = azimuthOptions.some(
            (option) => option.value === currentAzimuth
          )
            ? currentAzimuth.toString()
            : "0";
          mediaItemAzimuthSelectDom.addEventListener("change", (event) => {
            const selectedAzimuth = Number(
              (event.target as HTMLSelectElement).value
            );
            if (this.marbleImportMediaFiles[index]) {
              this.marbleImportMediaFiles[index].azimuth = selectedAzimuth;
            }
          });
          mediaItemDom.appendChild(mediaItemAzimuthSelectDom);

          const mediaItemDeleteDom = document.createElement("button");
          mediaItemDeleteDom.classList.add("marble-import-media-delete");
          const mediaItemDeleteIconDom = document.createElement("i");
          mediaItemDeleteIconDom.classList.add(
            "icon",
            "iconfont",
            "icon-cuowu1",
            "icon-close"
          );
          mediaItemDeleteDom.appendChild(mediaItemDeleteIconDom);
          mediaItemDeleteDom.addEventListener("click", () => {
            this.deleteMarbleMedia(fileitem.id);
          });
          mediaItemDom.appendChild(mediaItemDeleteDom);

          mediaListDom.appendChild(mediaItemDom);
        });
      }
    }
  }

  public deleteMarbleMedia(mediaId: string) {
    this.marbleImportMediaFiles = this.marbleImportMediaFiles.filter(
      (fileitem) => fileitem.id !== mediaId
    );
    this.updateMarbleImportMediaList();
  }

  handleMarbleImportUploadBtnClick(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    console.log("handleMarble ImportUploadBtn Click");
    const fileInputConfig = this.getMarbleMediaInputConfig();
    if (!fileInputConfig.canUpload) {
      this.showMsgDialog(
        getI18nText("modal.marbleImport.marbleMediaUploadLimit")
      );
      return;
    }
    const fileInput = document.createElement("input");

    fileInput.type = "file";
    fileInput.accept = fileInputConfig.accept;
    fileInput.multiple = fileInputConfig.maxUploadCount > 1;
    fileInput.maxLength = fileInputConfig.maxUploadCount;

    fileInput.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        if (files.length > fileInputConfig.maxUploadCount) {
          this.showMsgDialog(
            getI18nText("modal.marbleImport.marbleMediaUploadLimit")
          );
          return;
        }
        for (let index = 0; index < files.length; index++) {
          const file = files[index];
          const id = new Date().getTime().toString() + "_" + index;
          this.marbleImportMediaFiles.push({
            id: id, // 用时间戳+index
            file: file,
            media_asset_id: "",
            metadata: {},
            kind: file.type.includes("image") ? "image" : "video",
            extension: file.name.split(".").pop() || "",
            file_name: file.name,
            file_src: await getSrcByImgFile(file),
            azimuth: 0,
            // file_size: file.size,
          });
          console.log("[ModelDialogTools] select files----", {
            id: id,
            kind: file.type.includes("image") ? "image" : "video",
            extension: file.name.split(".").pop() || "",
            file_name: file.name,
          });
        }

        this.updateMarbleImportMediaList();
      }
      fileInput.value = "";
    };
    fileInput.click();
  }

  handleMarbleModelTypeChange(e: Event) {
    const modelType = (e.target as HTMLSelectElement).value;
    console.log("handle MarbleModelTypeChange--&&&&--", modelType);
  }

  private setMarbleModelTypeDropdownDisplay(modelType: string) {
    const marbleModelTypeButton = document.getElementById(
      "marbleImportModelTypeButton"
    ) as HTMLButtonElement | null;
    const dropdownItems = document.querySelectorAll(
      "#marbleImportModelTypeListDom .vision-dropdown-item"
    );
    const selectedItem = document.querySelector(
      `#marbleImportModelTypeListDom .vision-dropdown-item[data-value="${modelType}"]`
    ) as HTMLElement | null;
    const placeholder = getI18nText("modal.marbleImport.modelTypePlaceholder");

    if (marbleModelTypeButton) {
      marbleModelTypeButton.textContent =
        selectedItem?.textContent?.trim() || placeholder;
    }

    dropdownItems.forEach((item) => {
      const itemValue = item.getAttribute("data-value") || "";
      if (itemValue === modelType && modelType) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
    });
  }

  private initMarbleModelTypeDropdown() {
    const marbleModelTypeSelect = document.getElementById(
      "marbleImportModelTypeSelect"
    ) as HTMLSelectElement | null;
    const dropdownItems = document.querySelectorAll(
      "#marbleImportModelTypeListDom .vision-dropdown-item"
    );
    if (!marbleModelTypeSelect || dropdownItems.length === 0) return;

    dropdownItems.forEach((item) => {
      item.addEventListener("click", (event) => {
        event.preventDefault();
        const selectedValue = item.getAttribute("data-value") || "";
        if (marbleModelTypeSelect.value !== selectedValue) {
          marbleModelTypeSelect.value = selectedValue;
          marbleModelTypeSelect.dispatchEvent(new Event("change"));
        }
        this.setMarbleModelTypeDropdownDisplay(selectedValue);
      });
    });

    this.setMarbleModelTypeDropdownDisplay(marbleModelTypeSelect.value || "");
  }

  // Marble模型生成设置对话框
  initMarbleImportDialog() {
    if (!this.marbleImportModalDom) return;
    const marbleImportCancel = document.getElementById("marbleImportCancel");
    const marbleImportConfirm = document.getElementById("marbleImportConfirm");
    const marbleImportFormKeyInput = document.getElementById(
      "marbleImportFormKeyInput"
    ) as HTMLInputElement | null;
    const marbleImportUploadBtn = document.getElementById(
      "marbleImportUploadBtn"
    );
    const marbleImportUploadDom = document.getElementById(
      "marbleImportMediaDom"
    );
    const marbleModelTypeSelect = document.getElementById(
      "marbleImportModelTypeSelect"
    ) as HTMLInputElement | null;

    // 上传媒体资源，获取media_asset_id
    if (marbleImportUploadBtn) {
      marbleImportUploadBtn.addEventListener("click", (e) =>
        this.handleMarbleImportUploadBtnClick(e)
      );
    }
    if (marbleImportUploadDom) {
      marbleImportUploadDom.addEventListener("click", (e) =>
        this.handleMarbleImportUploadBtnClick(e)
      );
    }

    if (marbleModelTypeSelect) {
      marbleModelTypeSelect.addEventListener("change", (e) =>
        this.handleMarbleModelTypeChange(e)
      );
    }
    this.initMarbleModelTypeDropdown();
    // 取消按钮
    if (marbleImportCancel) {
      marbleImportCancel.addEventListener("click", () => {
        this.closeMarbleImportDialog();
      });
    }

    if (marbleImportFormKeyInput && marbleImportConfirm) {
      marbleImportFormKeyInput.addEventListener("change", async () => {
        await this.checkMarbleAPIKeyAvailable(
          marbleImportFormKeyInput,
          marbleImportConfirm as HTMLButtonElement
        );
      });
    }

    // 确认按钮
    if (marbleImportConfirm) {
      marbleImportConfirm.addEventListener("click", async () => {
        const formKeyInput = document.getElementById(
          "marbleImportFormKeyInput"
        ) as HTMLInputElement | null;
        const nameInput = document.getElementById(
          "marbleImportNameInput"
        ) as HTMLInputElement | null;
        const textPromptInput = document.getElementById(
          "marbleImportTextPromptInput"
        ) as HTMLTextAreaElement | null;
        const modelTypeValue = marbleModelTypeSelect
          ? marbleModelTypeSelect.value.trim()
          : "";
        const formKeyValue = formKeyInput ? formKeyInput.value.trim() : "";
        const textPromptValue = textPromptInput
          ? textPromptInput.value.trim()
          : "";
        const nameValue = nameInput ? nameInput.value.trim() : "";

        if (!formKeyValue) {
          this.showMsgDialog(
            getI18nText("modal.marbleImport.formKeyPlaceholder")
          );
          return;
        }
        if (!nameValue) {
          this.showMsgDialog(getI18nText("modal.marbleImport.namePlaceholder"));
          return;
        }
        if (!modelTypeValue) {
          this.showMsgDialog(
            getI18nText("modal.marbleImport.modelTypePlaceholder")
          );
          return;
        }
        if (!textPromptValue && this.marbleImportMediaFiles.length === 0) {
          this.showMsgDialog(
            getI18nText("modal.marbleImport.inputPromptPlaceholder")
          );
          return;
        }
        this.initMarbleApiController(formKeyValue);

        const result = await this.doUploadMarbleMedia(
          nameValue,
          textPromptValue
        );
        console.log("mediaInfoList----", result);
        this.closeMarbleImportDialog();
      });
    }
  }

  private async checkMarbleAPIKeyAvailable(
    formKeyInput: HTMLInputElement,
    marbleImportConfirm: HTMLButtonElement
  ) {
    const currentRequestId = ++this.marbleKeyCheckRequestId;
    const keyValue = formKeyInput.value.trim();
    const errorTipId = "marbleImportFormKeyInputErrorTip";
    const invalidKeyText = getI18nText("modal.marbleImport.invalidApiKey");
    const updateKeyInputValidationState = (isValid: boolean) => {
      let errorTipDom = document.getElementById(
        errorTipId
      ) as HTMLDivElement | null;
      if (isValid) {
        formKeyInput.setCustomValidity("");
        formKeyInput.classList.remove("marble-key-input-invalid");
        if (errorTipDom) {
          errorTipDom.remove();
        }
        return;
      }

      formKeyInput.setCustomValidity(invalidKeyText);
      formKeyInput.classList.add("marble-key-input-invalid");
      if (!errorTipDom) {
        errorTipDom = document.createElement("div");
        errorTipDom.id = errorTipId;
        errorTipDom.className = "marble-key-input-error-tip";
        formKeyInput.insertAdjacentElement("afterend", errorTipDom);
      }
      errorTipDom.textContent = invalidKeyText;
    };

    if (!keyValue) {
      marbleImportConfirm.disabled = true;
      updateKeyInputValidationState(false);
      return;
    }

    const isAvailable = await marble4ApiController.checkMarbleKeyAvailable(
      keyValue
    );
    if (currentRequestId !== this.marbleKeyCheckRequestId) return;
    console.log(
      "检查checkMarbleKey----Available apiKey是否有效-button disabled--",
      !isAvailable
    );
    marbleImportConfirm.disabled = !isAvailable;
    updateKeyInputValidationState(isAvailable);
  }

  initMarbleModelListDialog() {
    if (!this.marbleModelListModalDom) return;
    const marbleHistoryFormKeyInput = document.getElementById(
      "marbleHistoryFormKeyInput"
    ) as HTMLInputElement | null;
    const marbleHistorySearchBtn = document.getElementById(
      "marbleHistorySearchBtn"
    );
    if (marbleHistoryFormKeyInput) {
      // marbleHistoryFormKeyInput.value = '';
      marbleHistoryFormKeyInput.value = globalConfig.MARBLE_API_KEY;
      // if (marbleHistorySearchBtn) {
      //     void this.checkMarbleAPIKeyAvailable(marbleHistoryFormKeyInput, marbleHistorySearchBtn as HTMLButtonElement);
      // }
      // marbleHistoryFormKeyInput.addEventListener('change', async () => {
      //     if (!marbleHistorySearchBtn) return;
      //     await this.checkMarbleAPIKeyAvailable(marbleHistoryFormKeyInput, marbleHistorySearchBtn as HTMLButtonElement);
      // });
    }

    if (marbleHistorySearchBtn) {
      marbleHistorySearchBtn.addEventListener("click", () => {
        const formKeyValue = marbleHistoryFormKeyInput
          ? marbleHistoryFormKeyInput.value.trim()
          : "";
        console.log(`开始查询${formKeyValue}`);
        if (this.marbleListScene) {
          this.resetMarbleHistoryList(this.marbleListScene);
        } else {
          console.error("[MarbleHistory] marbleListScene is null");
          return;
        }
      });
    }
    const marbleModelListCancel = document.getElementById(
      "marbleModelListCancel"
    );
    // const marbleModelListConfirm = document.getElementById('marbleModelListConfirm');

    if (this.marbleHistoryLoadMoreBtn) {
      this.marbleHistoryLoadMoreBtn.addEventListener("click", () => {
        this.updateMarbleHistoryList(this.marbleListScene);
      });
    }

    if (marbleModelListCancel) {
      marbleModelListCancel.addEventListener("click", () => {
        this.closeMarbleModelListDialog();
      });
    }
    // if (marbleModelListConfirm) {
    //     marbleModelListConfirm.addEventListener('click', () => {
    //         this.closeMarbleModelListDialog();
    //     });
    // }
  }

  private resolveMarbleImageUrl(rawUrl: string): string {
    if (!rawUrl) return "";
    try {
      const parsedUrl = new URL(rawUrl, window.location.origin);
      const isHttp =
        parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
      const isCrossOrigin = parsedUrl.origin !== window.location.origin;
      if (import.meta.env.DEV && isHttp && isCrossOrigin) {
        return `/__marble_cdn_proxy${parsedUrl.pathname}${parsedUrl.search}`;
      }
      return parsedUrl.toString();
    } catch (error) {
      console.warn(
        "[MarbleHistory] invalid image url, use raw value",
        rawUrl,
        error
      );
      return rawUrl;
    }
  }

  createMarbleModelItem(modelInfo: MarbleModelInfo, scene: VisionaryScene) {
    const marbleModelItemDom = document.createElement("div");
    marbleModelItemDom.id = `marbleModelItem_${modelInfo.id}`;
    marbleModelItemDom.classList.add("marble-history-list-item");
    marbleModelItemDom.setAttribute("data-model-id", modelInfo.id);

    const imageWrap = document.createElement("div");
    imageWrap.classList.add("marble-history-list-item-image");

    const imageDom = document.createElement("img");
    imageDom.src = this.resolveMarbleImageUrl(modelInfo.image || "");
    imageDom.alt = modelInfo.name || "marble model image";

    imageDom.addEventListener("error", () => {
      console.error(`[MarbleHistory] image failed: ${imageDom.src}`);
    });

    const addButton = document.createElement("button");
    addButton.type = "button";
    addButton.classList.add("marble-history-add-btn");
    addButton.textContent = getI18nText("modal.marbleImport.add");
    addButton.setAttribute("data-i18n-text", "modal.marbleImport.add");
    addButton.addEventListener("click", () => {
      console.log(`点击添加事件${modelInfo.name}`, scene, modelInfo);
      if (modelInfo.srcurl) {
        scene.loadModel(modelInfo.srcurl, {
          displayName: modelInfo.name + ".spz",
        });
        this.closeMarbleModelListDialog();
      } else {
        console.error(`[MarbleHistory] model url not found: ${modelInfo.name}`);
        this.showMsgDialog(getI18nText("modal.marbleImport.modelUrlNotFound"));
        return;
      }
    });

    imageWrap.appendChild(imageDom);
    imageWrap.appendChild(addButton);

    const contentWrap = document.createElement("div");
    contentWrap.classList.add("history-item-content");

    const headerDom = document.createElement("div");
    headerDom.classList.add("history-item-content-header");

    const titleDom = document.createElement("span");
    titleDom.classList.add("history-item-content-title");
    titleDom.textContent = modelInfo.name || "";

    const dateDom = document.createElement("span");
    dateDom.classList.add("history-item-content-date");
    const createdAt = modelInfo.created_at;
    if (createdAt) {
      const createdAtDate = new Date(createdAt);
      dateDom.textContent = Number.isNaN(createdAtDate.getTime())
        ? createdAt
        : createdAtDate.toLocaleString("zh-CN", {
            timeZone: "Asia/Shanghai",
            hour12: false,
          });
    } else {
      dateDom.textContent = "";
    }

    headerDom.appendChild(titleDom);
    headerDom.appendChild(dateDom);

    const descriptionDom = document.createElement("div");
    descriptionDom.classList.add("history-item-content-description");
    const descriptionContentDom = document.createElement("div");
    descriptionContentDom.classList.add(
      "history-item-content-description-content"
    );
    const promptText = modelInfo.textPrompt || "";
    descriptionContentDom.textContent =
      getI18nText("modal.marbleImport.history.textPrompt") + promptText;
    descriptionDom.appendChild(descriptionContentDom);

    const itemBtnsDom = document.createElement("div");
    itemBtnsDom.classList.add("history-item-content-btns");
    const downloadButton = document.createElement("button");
    downloadButton.type = "button";
    downloadButton.classList.add("marble-history-download-btn");
    const downloadText = getI18nText("modal.marbleImport.download");
    downloadButton.title = downloadText;
    const downloadIcon = document.createElement("i");
    downloadIcon.classList.add("icon", "iconfont", "icon-xiazaidownload");
    downloadButton.append(downloadIcon);
    downloadButton.addEventListener("click", async () => {
      window.open(modelInfo.srcurl, "_blank");
    });
    itemBtnsDom.appendChild(downloadButton);

    const copyButton = document.createElement("button");
    copyButton.type = "button";
    copyButton.classList.add("marble-history-copy-btn");
    const copyText = getI18nText("modal.marbleImport.copy");
    copyButton.title = copyText;
    const copyIcon = document.createElement("i");
    copyIcon.classList.add("icon", "iconfont", "icon-CopyOutlined");
    copyButton.append(copyIcon);
    copyButton.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(promptText);
        this.showMsgDialog(getI18nText("modal.marbleImport.copySuccess"));
      } catch (error) {
        console.error("复制失败", error);
        this.showMsgDialog(getI18nText("modal.marbleImport.copyFailed"));
      }
    });

    itemBtnsDom.appendChild(copyButton);
    descriptionDom.appendChild(itemBtnsDom);

    contentWrap.appendChild(headerDom);
    contentWrap.appendChild(descriptionDom);

    marbleModelItemDom.appendChild(imageWrap);
    marbleModelItemDom.appendChild(contentWrap);
    return marbleModelItemDom;
  }

  initMarbleApiController(formKeyValue: string) {
    marble4ApiController.setApiKey(formKeyValue);
  }
  async doUploadMarbleMedia(name: string, textPrompt: string) {
    if (!marble4ApiController) {
      console.error("Marble API controller not initialized");
      throw new Error("Marble API controller not initialized");
    }

    const model =
      (
        document.getElementById(
          "marbleImportModelTypeSelect"
        ) as HTMLSelectElement | null
      )?.value ?? "";

    const mediaInfo = await marble4ApiController.generateMarbleModel(
      name,
      textPrompt,
      this.marbleImportMediaFiles,
      false,
      model
    );

    return mediaInfo;
  }

  async doSelectFolder(handle: FileSystemDirectoryHandle) {
    const sceneFS = globalManager.getSceneFS();
    if (!sceneFS) {
      console.warn("[Scene] SceneFS 未初始化，无法打开场景");
      this.showMsgDialog(getI18nText("errorinfo.sceneFSNotAvailable"));
      return;
    }
    try {
      console.log("Folder selected:", handle);
      await sceneFS.ensurePermission(handle, "readwrite");
      // 保存文件夹 handle
      this.sceneSaveModalFolderHandle = handle;
      const saveSceneFolderInput = document.getElementById(
        "saveSceneFolderInput"
      ) as HTMLInputElement | null;
      // 在输入框中显示文件夹名称
      if (saveSceneFolderInput) {
        saveSceneFolderInput.value = handle.name;
      }

      console.log("SceneFS: Folder selected for save:", handle.name);
    } catch (error) {
      if ((error as Error).name === "AbortError") {
        console.log("Folder selection cancelled by user");
      } else {
        console.error("Failed to select folder:", error);
      }
    }
  }

  // 示例场景对话框
  initDemoViewDialog() {
    if (!this.demoViewModalDom) return;
    const demoImageItems = document.querySelectorAll(
      ".vision-demoview-modal-dialog .vision-scene-image-item"
    );
    if (!demoImageItems || demoImageItems.length === 0) return;

    // 选择示例场景
    demoImageItems.forEach((item) => {
      item.addEventListener("click", () => {
        // 移除所有元素的 active class
        demoImageItems.forEach((el) => {
          (el as HTMLElement).classList.remove("active");
        });
        // 给当前元素添加 active class
        (item as HTMLElement).classList.add("active");
      });
    });

    // 取消按钮
    const demoViewCancel = document.getElementById("demoViewCancel");
    if (demoViewCancel) {
      demoViewCancel.addEventListener("click", () => {
        this.closeDemoViewDialog();
      });
    }

    // 确认按钮
    const demoViewConfirm = document.getElementById("demoViewConfirm");
    if (demoViewConfirm) {
      demoViewConfirm.addEventListener("click", () => {
        // 获取当前有 active class 的项
        const activeItem = document.querySelector(
          ".vision-demoview-modal-dialog .vision-scene-image-item.active"
        ) as HTMLElement | null;
        if (activeItem) {
          const video = activeItem.querySelector(
            "video"
          ) as HTMLVideoElement | null;
          const dataScene = video ? video.getAttribute("data-scene") || "" : "";
          console.log("选中的场景 data-scene:", dataScene);
          this.closeDemoViewDialog();
          openExampleViewPage(dataScene);
        } else {
          const selectExampleSceneTitle = getI18nText(
            "message.selectExampleScene.title"
          );
          const selectExampleSceneConfirm = getI18nText(
            "message.selectExampleScene.confirm"
          );
          this.showConfirmDialog(
            selectExampleSceneTitle,
            selectExampleSceneConfirm,
            () => {},
            () => {
              this.closeDemoViewDialog();
            }
          );
        }
      });
    }
  }

  // 退出视窗对话框
  initExistViewDialog() {
    const cancelBtn = document.getElementById("visionCancelBtn");
    const confirmBtn = document.getElementById("visionConfirmBtn");
    const optionItems = document.querySelectorAll(".vision-option-item");
    let selectedValue: ViewWindow = "left" as ViewWindow;

    // 重置选择
    optionItems.forEach((item) => {
      const el = item as HTMLElement;
      if (el.dataset.value === selectedValue) {
        el.classList.add("vision-option-selected");
      } else {
        el.classList.remove("vision-option-selected");
      }
    });

    // 选择选项
    optionItems.forEach((item) => {
      const el = item as HTMLElement;
      el.addEventListener("click", () => {
        selectedValue =
          (el.dataset.value as ViewWindow) || ("left" as ViewWindow);
        optionItems.forEach((opt) =>
          (opt as HTMLElement).classList.remove("vision-option-selected")
        );
        el.classList.add("vision-option-selected");
      });
    });

    // 关闭模态框
    const modal = this.exitViewModalDom;
    const closeModal = () => {
      if (modal) {
        modal.classList.remove("vision-modal-show");
      }
    };

    // 取消按钮
    if (cancelBtn) {
      cancelBtn.addEventListener("click", closeModal);
    }

    // 确认按钮
    if (confirmBtn) {
      confirmBtn.addEventListener("click", () => {
        console.log("选择的视窗:", selectedValue);
        if (
          globalTools.mainViewTools &&
          typeof globalTools.mainViewTools.removePanel === "function"
        ) {
          globalTools.mainViewTools.removePanel(selectedValue !== "left");
        }
        // 执行确认关闭视窗操作
        globalTools?.toolbarNavTools?.setSceneWindowSingle(true);
        closeModal();
        // 这里可以添加确认后的逻辑
      });
    }

    // 点击遮罩层关闭
    if (modal) {
      modal.addEventListener("click", (e) => {
        if (e.target === modal) {
          closeModal();
        }
      });
    }
  }

  // 打开视频设置模态框
  public openVideoDialog() {
    if (this.videoSettingsModalDom) {
      if (globalManager.getViewWindowIfSingle()) {
        const videoSettingsViewWindow = document.getElementById(
          "videoSettingsViewWindow"
        );
        const viewOptionItems =
          videoSettingsViewWindow?.querySelectorAll(
            ".vision-img-view-option-item"
          ) || [];
        viewOptionItems.forEach((item) => {
          const el = item as HTMLElement;
          el.classList.remove("vision-img-view-option-selected");
          if (el.dataset.value === "left") {
            el.classList.add("vision-img-view-option-selected");
          }
        });
      }
      this.videoSettingsModalDom.classList.add("vision-modal-show");
    }
  }
  // 关闭视频设置模态框
  public closeVideoDialog() {
    if (this.videoSettingsModalDom) {
      this.videoSettingsModalDom.classList.remove("vision-modal-show");
    }
  }
  // 打开图片设置模态框
  public openImageDialog() {
    if (this.imageSettingsModalDom) {
      if (globalManager.getViewWindowIfSingle()) {
        // 重置视窗设置单选框，默认选中"左"
        const imgSettingsViewWindow = document.getElementById(
          "imgSettingsViewWindow"
        );
        const viewOptionItems =
          imgSettingsViewWindow?.querySelectorAll(
            ".vision-img-view-option-item"
          ) || [];
        viewOptionItems.forEach((item) => {
          const el = item as HTMLElement;
          el.classList.remove("vision-img-view-option-selected");
          if (el.dataset.value === "left") {
            el.classList.add("vision-img-view-option-selected");
          }
        });
      }
      this.imageSettingsModalDom.classList.add("vision-modal-show");
    }
  }
  // 关闭图片设置模态框
  public closeImageDialog() {
    if (this.imageSettingsModalDom) {
      this.imageSettingsModalDom.classList.remove("vision-modal-show");
    }
  }
  // 打开场景新建模态框
  public openSceneDialog() {
    if (this.sceneSettingsModalDom) {
      this.sceneSettingsModalDom.classList.add("vision-modal-show");
    }
  }
  // 关闭场景新建模态框
  public closeSceneDialog() {
    if (this.sceneSettingsModalDom) {
      this.sceneSettingsModalDom.classList.remove("vision-modal-show");
    }
  }
  // 打开保存场景模态框
  public openSaveSceneDialog() {
    if (this.sceneSaveModalDom) {
      // 重置状态
      this.sceneSaveModalFolderHandle = null;

      // 清空输入框
      const saveSceneFolderInput = document.getElementById(
        "saveSceneFolderInput"
      ) as HTMLInputElement | null;
      const saveSceneNameInput = document.getElementById(
        "saveSceneNameInput"
      ) as HTMLInputElement | null;
      if (saveSceneFolderInput) {
        saveSceneFolderInput.value = "";
      }
      if (saveSceneNameInput) {
        saveSceneNameInput.value = "";
      }

      this.sceneSaveModalDom.classList.add("vision-modal-show");
    }
  }
  // 关闭保存场景模态框
  public closeSaveSceneDialog() {
    if (this.sceneSaveModalDom) {
      this.sceneSaveModalDom.classList.remove("vision-modal-show");
      // 清理状态
      this.sceneSaveModalFolderHandle = null;
    }
  }
  // 打开 Marble 模型生成设置模态框
  public openMarbleImportDialog() {
    if (this.marbleImportModalDom) {
      this.marbleImportMediaFiles = [];
      const marbleImportFormKeyInput = document.getElementById(
        "marbleImportFormKeyInput"
      ) as HTMLInputElement | null;
      const marbleImportConfirm = document.getElementById(
        "marbleImportConfirm"
      ) as HTMLButtonElement | null;
      const marbleImportNameInput = document.getElementById(
        "marbleImportNameInput"
      ) as HTMLInputElement | null;
      const marbleImportTextPromptInput = document.getElementById(
        "marbleImportTextPromptInput"
      ) as HTMLTextAreaElement | null;
      const marbleImportModelTypeSelect = document.getElementById(
        "marbleImportModelTypeSelect"
      ) as HTMLSelectElement | null;

      if (marbleImportFormKeyInput) {
        marbleImportFormKeyInput.value = globalConfig.MARBLE_API_KEY;
        if (
          marbleImportFormKeyInput.value.trim() !== "" &&
          marbleImportConfirm
        ) {
          void this.checkMarbleAPIKeyAvailable(
            marbleImportFormKeyInput,
            marbleImportConfirm
          );
        }
      }
      if (marbleImportNameInput) {
        marbleImportNameInput.value = "";
      }
      if (marbleImportTextPromptInput) {
        marbleImportTextPromptInput.value = "";
      }

      if (marbleImportModelTypeSelect) {
        marbleImportModelTypeSelect.value = "";
      }
      this.setMarbleModelTypeDropdownDisplay("");
      /////////////////////////////////// here for test 为开发方便，后续需要删除///////////////////////////////////

      if (import.meta.env.MODE === "development") {
        if (marbleImportNameInput) {
          marbleImportNameInput.value = "冬季场景";
        }
        if (marbleImportTextPromptInput) {
          marbleImportTextPromptInput.value = "生成冬天的景象";
        }
        if (marbleImportModelTypeSelect) {
          marbleImportModelTypeSelect.value = "marble-1.1-plus";
        }
      }
      /////////////////////////////////////////////////////////////////////////////////////////////

      this.updateMarbleImportMediaList();
      this.marbleImportModalDom.classList.add("vision-modal-show");
    }
  }
  // 关闭 Marble 模型生成设置模态框
  public closeMarbleImportDialog() {
    if (this.marbleImportModalDom) {
      this.marbleImportModalDom.classList.remove("vision-modal-show");
    }
  }
  // 打开 Marble 模型列表模态框
  public openMarbleModelListDialog(scene: VisionaryScene) {
    if (this.marbleModelListModalDom) {
      // 每次打开弹窗时重新获取，避免初始化时机导致的引用失效
      this.marbleHistoryListBodyDom = document.getElementById(
        "marbleHistoryListBody"
      );
      if (!this.marbleHistoryListBodyDom) {
        console.error(
          "[MarbleHistory] #marbleHistoryListBody not found when opening dialog"
        );
        return;
      }
      // 获取 Marble 模型列表，并初始化显示
      const marbleHistoryFormKeyInput = document.getElementById(
        "marbleHistoryFormKeyInput"
      ) as HTMLInputElement | null;
      if (marbleHistoryFormKeyInput) {
        marbleHistoryFormKeyInput.value = globalConfig.MARBLE_API_KEY;
      }
      // if (this.marbleHistoryLoadMoreBtn) {
      //     this.marbleHistoryLoadMoreBtn.style.display = 'block';
      // }
      this.marbleListScene = scene;
      this.resetMarbleHistoryList(this.marbleListScene);
      this.marbleModelListModalDom.classList.add("vision-modal-show");
    }
  }
  public async updateMarbleHistoryList(scene: VisionaryScene | null) {
    if (!scene) {
      console.error("[MarbleHistory] update aborted: scene is null");
      return;
    }
    // 强制在使用前校验并兜底重新查询
    if (!this.marbleHistoryListBodyDom) {
      this.marbleHistoryListBodyDom = document.getElementById(
        "marbleHistoryListBody"
      );
    }
    if (!this.marbleHistoryListBodyDom) {
      console.error(
        "[MarbleHistory] update aborted: #marbleHistoryListBody is null"
      );
      return;
    }
    const listBodyDom = this.marbleHistoryListBodyDom;

    // marbleHistoryListBodyDom
    const marbleModelList = [];
    const marbleHistoryFormKeyInput = document.getElementById(
      "marbleHistoryFormKeyInput"
    ) as HTMLInputElement | null;
    if (
      marbleHistoryFormKeyInput &&
      marbleHistoryFormKeyInput.value.trim() !== ""
    ) {
      // 查询 Marble 模型列表
      marble4ApiController.setApiKey(marbleHistoryFormKeyInput.value.trim());
      let marbleModelList;
      try {
        marbleModelList = await marble4ApiController.listWorlds({
          page_size: 5,
          page_token: this.marbleModelListPageToken,
        });
      } catch (error) {
        console.error("[MarbleHistory] listWorlds failed", error);
        if (this.marbleHistoryLoadMoreBtn) {
          this.marbleHistoryLoadMoreBtn.style.display = "none";
        }
        this.showMsgDialog(
          getI18nText("modal.marbleImport.modelListQueryFailed")
        );
        return;
      }

      this.marbleModelListPageToken = marbleModelList.next_page_token || null;
      let hasNew = false;
      if (
        marbleModelList &&
        marbleModelList.worlds &&
        marbleModelList.worlds.length > 0
      ) {
        marbleModelList.worlds.forEach((item) => {
          if (
            item.assets &&
            marble4ApiController.checkMarbleModelAsset(item.assets)
          ) {
            const modelDom = document.getElementById(
              `marbleModelItem_${item.world_id}`
            );
            if (!modelDom) {
              const marbleModelInfo: MarbleModelInfo = {
                id: item.world_id,
                name: item.display_name,
                image: item.assets?.thumbnail_url || "",
                status: "completed",
                textPrompt:
                  (item.world_prompt as TextWorldPrompt).text_prompt || "",
                created_at: item.created_at || "",
                updated_at: item.updated_at || "",
                srcurl: item.assets?.splats?.spz_urls?.full_res || "",
              };
              const marbleModelItem = this.createMarbleModelItem(
                marbleModelInfo,
                scene
              );
              listBodyDom.appendChild(marbleModelItem);
              hasNew = true;
            }
          }
        });
      } else {
        console.warn("[MarbleHistory] listWorlds returned empty worlds");
      }
      if (
        (!hasNew || marbleModelList.worlds.length < 5) &&
        this.marbleHistoryLoadMoreBtn
      ) {
        this.marbleHistoryLoadMoreBtn.style.display = "none";
      }
    } else {
      console.warn("[MarbleHistory] form key is empty, skip list query");
    }
  }

  public resetMarbleHistoryList(scene: VisionaryScene) {
    // 强制在使用前校验并兜底重新查询
    if (!this.marbleHistoryListBodyDom) {
      this.marbleHistoryListBodyDom = document.getElementById(
        "marbleHistoryListBody"
      );
    }
    if (!this.marbleHistoryListBodyDom) {
      console.error(
        "[MarbleHistory] update aborted: #marbleHistoryListBody is null"
      );
      return;
    }
    const listBodyDom = this.marbleHistoryListBodyDom;
    listBodyDom.innerHTML = "";
    if (this.marbleHistoryLoadMoreBtn) {
      this.marbleHistoryLoadMoreBtn.style.display = "block";
    }
    this.marbleModelListPageToken = null;
    this.updateMarbleHistoryList(scene);
  }
  // 关闭 Marble 模型列表模态框
  public closeMarbleModelListDialog() {
    console.log("closeMarbleModelListDialog", this.marbleModelListModalDom);
    if (this.marbleModelListModalDom) {
      this.marbleModelListModalDom.classList.remove("vision-modal-show");
    }
  }
  // 打开示例场景模态框
  public openDemoViewDialog() {
    if (this.demoViewModalDom) {
      this.demoViewModalDom.classList.add("vision-modal-show");
      const demoImageItems = document.querySelectorAll(
        ".vision-demoview-modal-dialog .vision-scene-image-item"
      );
      if (!demoImageItems || demoImageItems.length === 0) return;
      demoImageItems.forEach((item) => {
        item.classList.remove("active");
      });
      (demoImageItems[0] as HTMLElement).classList.add("active");
    }
  }
  // 关闭示例场景模态框
  public closeDemoViewDialog() {
    if (this.demoViewModalDom) {
      this.demoViewModalDom.classList.remove("vision-modal-show");
    }
  }
  // 打开退出视窗模态框
  public openExitViewDialog() {
    if (this.exitViewModalDom) {
      this.exitViewModalDom.classList.add("vision-modal-show");
    }
  }
  // 关闭退出视窗模态框
  public closeExitViewDialog() {
    if (this.exitViewModalDom) {
      this.exitViewModalDom.classList.remove("vision-modal-show");
    }
  }

  // 初始化加载信息模态框
  initLoadingInfoModal() {
    // 加载信息模态框通常不需要额外的交互，只需要显示和隐藏
    // 可以通过点击遮罩层关闭（如果需要的话）
    // 注意：通常加载模态框不应该可以通过点击遮罩层关闭
  }

  // 打开加载信息模态框
  public showLoadingInfoModal(
    text: string = "加载中...",
    isHtml: boolean = false
  ) {
    if (this.loadingInfoModalDom) {
      const textElement = document.getElementById("loadingInfoText");
      if (textElement) {
        if (!isHtml) {
          textElement.textContent = text;
        } else {
          textElement.innerHTML = text;
        }
      }
      // console.log('【显示提示蒙版】:', text, new Date().getTime());
      this.loadingInfoModalDom.classList.add("vision-modal-show");
    }
  }

  // 更新加载信息模态框
  public updateLoadingInfoModal(text: string = "加载中...") {
    if (this.loadingInfoModalDom) {
      const textElement = document.getElementById("loadingInfoText");
      if (textElement) {
        textElement.textContent = text;
      }
    }
  }

  // 关闭加载信息模态框
  public closeLoadingInfoModal(timeout: number = 0) {
    if (this.loadingInfoModalDom) {
      if (timeout > 0) {
        setTimeout(() => {
          if (this.loadingInfoModalDom) {
            // console.log('【关闭提示蒙版】', timeout, new Date().getTime());
            this.loadingInfoModalDom.classList.remove("vision-modal-show");
          }
        }, 1000 * timeout);
      } else {
        // console.log('【关闭提示蒙版】', timeout, new Date().getTime());
        this.loadingInfoModalDom.classList.remove("vision-modal-show");
      }
    }
  }

  public showConfirmDialog(
    title: string,
    message: string,
    onConfirm?: () => void,
    onCancel?: () => void
  ) {
    if (!this.confirmModalDom) {
      console.warn("Confirm modal element not found");
      return;
    }

    this.cleanupConfirmDialogHandlers();

    const titleElement = this.confirmModalDom.querySelector(
      "#modelDeleteConfirmLabel"
    );
    const messageElement = this.confirmModalDom.querySelector(
      "#modelDeleteConfirmMessage"
    );
    const cancelButton = this.confirmModalDom.querySelector(
      '[data-role="cancel"]'
    );
    const confirmButton = this.confirmModalDom.querySelector(
      '[data-role="confirm"]'
    );
    const closeButton = this.confirmModalDom.querySelector(".btn-close");

    if (titleElement) {
      (titleElement as HTMLElement).textContent = title;
    }
    if (messageElement) {
      (messageElement as HTMLElement).textContent = message;
    }

    this.confirmOnCancel = typeof onCancel === "function" ? onCancel : null;
    this.confirmLastAction = "dismiss";

    if (cancelButton && this.confirmCancelHandler) {
      cancelButton.removeEventListener("click", this.confirmCancelHandler);
    }
    this.confirmCancelHandler = () => {
      this.confirmLastAction = "cancel";
      if (typeof onCancel === "function") {
        onCancel();
      }
      this.closeConfirmDialog();
    };
    if (cancelButton) {
      cancelButton.addEventListener("click", this.confirmCancelHandler);
    }

    if (confirmButton && this.confirmConfirmHandler) {
      confirmButton.removeEventListener("click", this.confirmConfirmHandler);
    }
    this.confirmConfirmHandler = () => {
      this.confirmLastAction = "confirm";
      if (typeof onConfirm === "function") {
        onConfirm();
      }
      this.closeConfirmDialog();
    };
    if (confirmButton) {
      confirmButton.addEventListener("click", this.confirmConfirmHandler);
    }

    if (closeButton && this.confirmCloseHandler) {
      closeButton.removeEventListener("click", this.confirmCloseHandler);
    }
    this.confirmCloseHandler = () => {
      this.confirmLastAction = "cancel";
      if (typeof onCancel === "function") {
        onCancel();
      }
      this.closeConfirmDialog();
    };
    if (closeButton) {
      closeButton.addEventListener("click", this.confirmCloseHandler);
    }

    if (this.confirmModalDom && this.confirmHiddenHandler) {
      this.confirmModalDom.removeEventListener(
        "hidden.bs.modal",
        this.confirmHiddenHandler as EventListener
      );
    }
    this.confirmHiddenHandler = () => {
      if (this.confirmLastAction === "dismiss" && this.confirmOnCancel) {
        this.confirmOnCancel();
      }
      this.cleanupConfirmDialogHandlers();
    };
    this.confirmModalDom.addEventListener(
      "hidden.bs.modal",
      this.confirmHiddenHandler as EventListener
    );

    if (
      !this.confirmModalInstance &&
      typeof bootstrap !== "undefined" &&
      bootstrap &&
      typeof bootstrap.Modal === "function"
    ) {
      this.confirmModalInstance = new bootstrap.Modal(this.confirmModalDom);
    }

    if (
      this.confirmModalInstance &&
      typeof this.confirmModalInstance.show === "function"
    ) {
      this.confirmModalInstance.show();
    } else {
      this.confirmModalDom.classList.add("show", "vision-modal-show");
      this.confirmModalDom.style.display = "block";
    }
  }

  // 显示/隐藏Notice消息模态框
  public setNoticeMessageModal(visible: boolean) {
    const noticeMessageModalDom = document.getElementById("noticeMessageModal");
    if (!noticeMessageModalDom) {
      console.warn("Notice message modal element not found");
      return;
    }
    if (visible) {
      noticeMessageModalDom.classList.add("vision-modal-show");
    } else {
      noticeMessageModalDom.classList.remove("vision-modal-show");
    }
  }
  // 显示/隐藏关于消息模态框
  public setAboutMessageModal(visible: boolean) {
    const aboutMessageModalDom = document.getElementById("aboutMessageModal");
    if (!aboutMessageModalDom) {
      console.warn("About message modal element not found");
      return;
    }
    if (visible) {
      // 打开模态框时重置复选框状态
      const aboutNotShowAgainCheckbox = document.getElementById(
        "aboutNotShowAgain"
      ) as HTMLInputElement | null;
      if (aboutNotShowAgainCheckbox) {
        aboutNotShowAgainCheckbox.checked = false;
      }
      aboutMessageModalDom.classList.add("vision-modal-show");
    } else {
      aboutMessageModalDom.classList.remove("vision-modal-show");
    }
  }

  public showMessageModal(params: {
    title: string;
    message: string;
    duration: number;
  }) {
    if (!this.showMessageModalDom) {
      console.warn("Show message modal element not found");
      return;
    }

    // 清理之前的处理器和定时器
    this.cleanupShowMessageModalHandlers();
    if (this.showMessageModalTimeout) {
      clearTimeout(this.showMessageModalTimeout);
      this.showMessageModalTimeout = null;
    }

    const titleElement = this.showMessageModalDom.querySelector(
      "#modelShowMessageLabel"
    );
    const messageElement = this.showMessageModalDom.querySelector(
      "#modelShowMessageMessage"
    );
    const confirmButton = this.showMessageModalDom.querySelector(
      '[data-role="confirm"]'
    );
    const closeButton = this.showMessageModalDom.querySelector(".btn-close");

    if (titleElement) {
      (titleElement as HTMLElement).textContent = params.title;
    }
    if (messageElement) {
      (messageElement as HTMLElement).textContent = params.message;
    }

    // 设置确认按钮事件处理器
    if (confirmButton && this.showMessageModalConfirmHandler) {
      confirmButton.removeEventListener(
        "click",
        this.showMessageModalConfirmHandler
      );
    }
    this.showMessageModalConfirmHandler = () => {
      this.closeShowMessageModal();
    };
    if (confirmButton) {
      confirmButton.addEventListener(
        "click",
        this.showMessageModalConfirmHandler
      );
    }

    // 设置关闭按钮事件处理器
    if (closeButton && this.showMessageModalCloseHandler) {
      closeButton.removeEventListener(
        "click",
        this.showMessageModalCloseHandler
      );
    }
    this.showMessageModalCloseHandler = () => {
      this.closeShowMessageModal();
    };
    if (closeButton) {
      closeButton.addEventListener("click", this.showMessageModalCloseHandler);
    }

    // 设置隐藏事件处理器
    if (this.showMessageModalDom) {
      if (this.showMessageModalHiddenHandler) {
        this.showMessageModalDom.removeEventListener(
          "hidden.bs.modal",
          this.showMessageModalHiddenHandler as EventListener
        );
      }
      this.showMessageModalHiddenHandler = () => {
        this.cleanupShowMessageModalHandlers();
      };
      this.showMessageModalDom.addEventListener(
        "hidden.bs.modal",
        this.showMessageModalHiddenHandler as EventListener
      );
    }

    // 初始化 Bootstrap Modal 实例
    if (
      !this.showMessageModalInstance &&
      typeof bootstrap !== "undefined" &&
      bootstrap &&
      typeof bootstrap.Modal === "function"
    ) {
      this.showMessageModalInstance = new bootstrap.Modal(
        this.showMessageModalDom
      );
    }

    // 显示模态框
    if (
      this.showMessageModalInstance &&
      typeof this.showMessageModalInstance.show === "function"
    ) {
      this.showMessageModalInstance.show();
    } else {
      this.showMessageModalDom.classList.add("show", "vision-modal-show");
      this.showMessageModalDom.style.display = "block";
    }

    // 如果 duration > 0，设置自动关闭定时器
    if (params.duration > 0) {
      this.showMessageModalTimeout = setTimeout(() => {
        this.closeShowMessageModal();
      }, 1000 * params.duration);
    }
  }
  public closeConfirmDialog() {
    if (!this.confirmModalDom) {
      return;
    }

    if (
      this.confirmModalInstance &&
      typeof this.confirmModalInstance.hide === "function"
    ) {
      this.confirmModalInstance.hide();
    } else {
      this.confirmModalDom.classList.remove("show", "vision-modal-show");
      this.confirmModalDom.style.display = "none";
    }

    this.cleanupConfirmDialogHandlers();
  }
  public closeShowMessageModal() {
    if (!this.showMessageModalDom) {
      return;
    }

    // 清除自动关闭定时器
    if (this.showMessageModalTimeout) {
      clearTimeout(this.showMessageModalTimeout);
      this.showMessageModalTimeout = null;
    }

    // 使用 Bootstrap Modal 关闭
    if (
      this.showMessageModalInstance &&
      typeof this.showMessageModalInstance.hide === "function"
    ) {
      this.showMessageModalInstance.hide();
    } else {
      this.showMessageModalDom.classList.remove("show", "vision-modal-show");
      this.showMessageModalDom.style.display = "none";
    }

    // 清理事件处理器
    this.cleanupShowMessageModalHandlers();
  }

  private cleanupConfirmDialogHandlers() {
    if (!this.confirmModalDom) {
      this.confirmCancelHandler = null;
      this.confirmConfirmHandler = null;
      this.confirmCloseHandler = null;
      this.confirmHiddenHandler = null;
      this.confirmOnCancel = null;
      this.confirmLastAction = "dismiss";
      return;
    }

    const cancelButton = this.confirmModalDom.querySelector(
      '[data-role="cancel"]'
    );
    const confirmButton = this.confirmModalDom.querySelector(
      '[data-role="confirm"]'
    );
    const closeButton = this.confirmModalDom.querySelector(".btn-close");

    if (cancelButton && this.confirmCancelHandler) {
      cancelButton.removeEventListener("click", this.confirmCancelHandler);
    }
    if (confirmButton && this.confirmConfirmHandler) {
      confirmButton.removeEventListener("click", this.confirmConfirmHandler);
    }
    if (closeButton && this.confirmCloseHandler) {
      closeButton.removeEventListener("click", this.confirmCloseHandler);
    }
    if (this.confirmHiddenHandler) {
      this.confirmModalDom.removeEventListener(
        "hidden.bs.modal",
        this.confirmHiddenHandler as EventListener
      );
    }

    this.confirmCancelHandler = null;
    this.confirmConfirmHandler = null;
    this.confirmCloseHandler = null;
    this.confirmHiddenHandler = null;
    this.confirmOnCancel = null;
    this.confirmLastAction = "dismiss";
  }

  private cleanupShowMessageModalHandlers() {
    if (!this.showMessageModalDom) {
      this.showMessageModalConfirmHandler = null;
      this.showMessageModalCloseHandler = null;
      this.showMessageModalHiddenHandler = null;
      return;
    }

    const confirmButton = this.showMessageModalDom.querySelector(
      '[data-role="confirm"]'
    );
    const closeButton = this.showMessageModalDom.querySelector(".btn-close");

    if (confirmButton && this.showMessageModalConfirmHandler) {
      confirmButton.removeEventListener(
        "click",
        this.showMessageModalConfirmHandler
      );
    }
    if (closeButton && this.showMessageModalCloseHandler) {
      closeButton.removeEventListener(
        "click",
        this.showMessageModalCloseHandler
      );
    }
    if (this.showMessageModalHiddenHandler) {
      this.showMessageModalDom.removeEventListener(
        "hidden.bs.modal",
        this.showMessageModalHiddenHandler as EventListener
      );
    }

    this.showMessageModalConfirmHandler = null;
    this.showMessageModalCloseHandler = null;
    this.showMessageModalHiddenHandler = null;
  }

  /**
   * 检查是否应该显示关于信息模态框
   * @returns 如果用户选择了"不再提醒"，返回 false；否则返回 true
   */
  public shouldShowNoticeMessage(): boolean {
    const notShowAgain = localStorage.getItem("noticeMessageNotShowAgain");
    return notShowAgain !== "true";
  }

  public initNoticeMessageModalShow() {
    // 检查用户是否选择了"不再提醒"
    // if (!this.shouldShowNoticeMessage()) {
    //     console.log('用户选择了不再提醒，跳过显示信息提示模态框');
    //     return;
    // }

    console.log("initNoticeMessageModalShow", new Date().getTime());
    const noticeMessageTitleDom = document.getElementById(
      "modelShowNoticeTitle"
    );
    if (noticeMessageTitleDom) {
      noticeMessageTitleDom.textContent = getI18nText("modal.about.title");
    }
    const noticeMessageContent1Dom = document.getElementById(
      "noticeMessageContent1"
    );
    if (noticeMessageContent1Dom) {
      noticeMessageContent1Dom.textContent = getI18nText(
        "modal.about.message-1.content"
      );
    }
    const noticeMessageContent2Dom = document.getElementById(
      "noticeMessageContent2"
    );
    if (noticeMessageContent2Dom) {
      noticeMessageContent2Dom.textContent = getI18nText(
        "modal.about.message-2.content"
      );
    }
    const noticeMessageContent3Item1Dom = document.getElementById(
      "noticeMessageContent3Item1"
    );
    if (noticeMessageContent3Item1Dom) {
      noticeMessageContent3Item1Dom.textContent = getI18nText(
        "modal.about.message-3.content.item-1"
      );
    }
    const noticeMessageContent3Item2Dom = document.getElementById(
      "noticeMessageContent3Item2"
    );
    if (noticeMessageContent3Item2Dom) {
      noticeMessageContent3Item2Dom.textContent = getI18nText(
        "modal.about.message-3.content.item-2"
      );
    }
    const noticeMessageContent5Dom = document.getElementById(
      "noticeMessageContent5"
    );
    if (noticeMessageContent5Dom) {
      noticeMessageContent5Dom.textContent = getI18nText(
        "modal.about.message-5.content"
      );
    }
    const noticeMessageContent5DetailItem1Dom = document.getElementById(
      "noticeMessageContent5DetailItem1"
    );
    if (noticeMessageContent5DetailItem1Dom) {
      noticeMessageContent5DetailItem1Dom.textContent = getI18nText(
        "modal.about.message-5.detail.item-1"
      );
    }
    const noticeMessageContent5DetailItem2Dom = document.getElementById(
      "noticeMessageContent5DetailItem2"
    );
    if (noticeMessageContent5DetailItem2Dom) {
      noticeMessageContent5DetailItem2Dom.textContent = getI18nText(
        "modal.about.message-5.detail.item-2"
      );
    }
    const noticeMessageConfirmBtnDom = document.getElementById(
      "noticeMessageConfirmBtn"
    );
    if (noticeMessageConfirmBtnDom) {
      noticeMessageConfirmBtnDom.textContent = getI18nText(
        "modal.about.confirm"
      );
    }
    this.setNoticeMessageModal(true);
    console.log("initNoticeMessageModalShow end", new Date().getTime());
  }

  showMsgDialog(msg: string, duration: number = 2) {
    if (!this.showMsgUI) {
      this.showMsgUI = createShowMsgUI();
    }
    this.showMsgUI.showInfo(msg, duration);
  }
}

// export { ModelDialogTools };
